/**
 * Which top-level section owns each URL. Drives the highlighted menu item,
 * the "Back to…" link, breadcrumbs, the human sitemap and docs/ROUTE_OWNERSHIP.md.
 * Add new routes here when they don't match an existing prefix.
 */
import { LEARN_LINKS } from "@/config/learnNav";

export type Section = "Dashboard" | "Chat" | "Tools" | "Learn" | "More";

export const SECTION_HOME: Record<Section, string> = {
  Dashboard: "/dashboard",
  Chat: "/chat",
  Tools: "/tools",
  Learn: "/learn",
  More: "/site-map",
};

/** Prefix rules, checked in order. A prefix matches itself or `${prefix}/…`. */
const RULES: { section: Section; prefixes: string[] }[] = [
  { section: "Dashboard", prefixes: ["/dashboard", "/my-stuff", "/wrapped", "/genie-trails"] },
  { section: "Chat", prefixes: ["/chat", "/talk-to-geniebot"] },
  {
    section: "Learn",
    prefixes: [
      "/learn",
      "/ai-video-lab",
      "/blog",
      "/blogs",
      "/course",
      "/courses",
      "/thinking-academy",
      "/prompt-training",
      "/security-guide",
      "/security-checklist",
      "/training",
      "/tia",
      ...LEARN_LINKS.map((l) => l.href),
    ],
  },
  {
    section: "Tools",
    prefixes: [
      "/tools",
      "/tool-finder",
      "/ai-",
      "/prompt-",
      "/prompts",
      "/pic-editor",
      "/geniebot-",
      "/games",
      "/pipelines",
      "/chatbot-builder",
      "/contract-analyzer",
      "/pdf-storybook",
      "/resume-builder",
      "/college-ready",
      "/prompt-cube",
      "/cyber-kitchen",
      "/workflow-builder",
      "/diversity-mode",
    ],
  },
  {
    section: "More",
    prefixes: ["/help", "/profile-settings", "/settings", "/accessibility-settings", "/analytics", "/changelog", "/about", "/site-map", "/legal", "/booking-history", "/book-consultation", "/affiliate", "/theme-lab"],
  },
];

const matches = (path: string, prefix: string) =>
  prefix.endsWith("-") ? path.startsWith(prefix) : path === prefix || path.startsWith(prefix + "/");

export const sectionForPath = (pathname: string): Section | null => {
  const path = pathname.replace(/\/+$/, "") || "/";
  if (path === "/") return null;
  for (const rule of RULES) if (rule.prefixes.some((p) => matches(path, p))) return rule.section;
  return null;
};

/** Where "Back to…" should go. Section home, or the parent path for deep pages inside a section home. */
export const backTargetFor = (pathname: string): { label: string; href: string } | null => {
  const section = sectionForPath(pathname);
  if (!section) return null;
  const home = SECTION_HOME[section];
  if (pathname === home) return null;
  return { label: section === "More" ? "All pages" : section, href: home };
};
